import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import type { Instrument } from "../types";

/**
 * Typeahead over the uploaded instrument universe.
 *
 * Searches ticker *and* company name, because a mistyped symbol doesn't error —
 * it silently creates a second, unrelated "position" that never reconciles.
 *
 * Free text is still allowed: a symbol missing from the uploaded file (a recent
 * listing, say) must not block recording a real trade.
 */
export function SymbolSearch({
  value,
  onChange,
  autoFocus,
}: {
  value: string;
  onChange: (symbol: string) => void;
  autoFocus?: boolean;
}) {
  const [query, setQuery] = useState(value);
  const [results, setResults] = useState<Instrument[]>([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => setQuery(value), [value]);

  useEffect(() => {
    if (!open || query.trim().length < 1) {
      setResults([]);
      return;
    }
    let cancelled = false;
    // Debounced: this fires on every keystroke otherwise.
    const timer = setTimeout(() => {
      api
        .searchInstruments(query)
        .then((r) => !cancelled && setResults(r))
        .catch(() => !cancelled && setResults([]));
    }, 180);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, open]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const choose = (instrument: Instrument) => {
    onChange(instrument.symbol);
    setQuery(instrument.symbol);
    setOpen(false);
  };

  return (
    <div className="typeahead" ref={box}>
      <input
        autoFocus={autoFocus}
        value={query}
        placeholder="Symbol or company name"
        onChange={(e) => {
          const next = e.target.value.toUpperCase();
          setQuery(next);
          onChange(next);
          setOpen(true);
          setHighlight(0);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (!open || results.length === 0) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlight((h) => Math.min(h + 1, results.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, 0));
          } else if (e.key === "Enter") {
            e.preventDefault();
            choose(results[highlight]);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
      />

      {open && results.length > 0 && (
        <ul className="typeahead-list">
          {results.map((instrument, index) => (
            <li
              key={instrument.id}
              className={index === highlight ? "active" : ""}
              onMouseEnter={() => setHighlight(index)}
              onMouseDown={(e) => {
                e.preventDefault();
                choose(instrument);
              }}
            >
              <strong>{instrument.symbol}</strong>
              {instrument.name && <span className="muted"> · {instrument.name}</span>}
              {instrument.sector && <span className="tag">{instrument.sector}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
